import React, { useEffect, useRef, useState } from "react";
import { Heart, MessageCircle, Pause, Play, Send, RotateCcw } from "lucide-react";
import { PostActions, usePostSocial } from "./ChapterCarousel";
import { CalendlyLink } from "./SiteHeader";
import { Intro } from "./sections";
import { FACTORS } from "./RatesChrome";
import { CLOSING_COST_CARDS } from "./socialThreads";

const HANDLE = "hellomortgage.ca";
const UNCLE = "/brand/uncle-avatar.jpg";
const HELLO = "/brand/social-avatar.png";
const MYTHS = [
  "Put more down. Less down means a worse rate, obviously.",
  "Credit score doesn't matter if you've got a steady job.",
  "A mortgage is a mortgage. House, condo, rental—same rate.",
  "Just get the 5-year fixed like everyone else.",
  "Lowest rate wins. End of story.",
  "I got 2.79% back in the day. You kids are getting robbed.",
];
const LIKES = [214, 187, 96, 158, 302, 441];
export const uncleItems = (place) => FACTORS.map(([k, h, body], i) => ({ k, h, body: body.replace("We ran that math for you above.", ""), myth: MYTHS[i], likes: LIKES[i], place }));

/* ---------- a) Instagram-style comments sheet ---------- */
export const UNCLE_THREAD = [
  { uncle: "I got 2.79% back in the day. You kids are getting robbed.", age: "7h", likes: 441,
    lead: "2.79%? We miss her too. Unfortunately, nostalgia isn't an approved mortgage strategy.",
    reply: "Your rate is from the day you signed—not today. Rates move with bond yields and the Bank of Canada, sometimes inside a week, so the number from your spring isn't on the shelf anymore. What is on the shelf: a free 120-day rate hold. Lock today's price, shop in peace, and if rates drop before closing you get the lower one. It's the closest thing to 2.79% we can legally offer.",
    reactions: [["yeg.homebuyer", "🔥🔥🔥 \"legally offer\""], ["prairie_dad_", "👏👏 tell him"]] },
  { uncle: "Put more down. Less down means a worse rate, obviously.", age: "6h", likes: 214,
    lead: "That would make sense. Unfortunately, Canadian mortgages have never let common sense get in the way of a good plot twist.",
    reply: "Under 20% down the mortgage is insured, so the lender's risk is covered and they price it sharper. Put 20%+ down and you skip the insurance premium, but the rate itself usually runs a touch higher. Neither is 'better'—they're different math, and we run both before anyone picks.",
    reactions: [["firsthome.finally", "🙌 wait WHAT"]] },
  { uncle: "Lowest rate wins. End of story.", age: "5h", likes: 302,
    lead: "Lowest rate wins the screenshot. The mortgage strategy wins the next five years.",
    reply: "Penalty formulas, prepayment room, portability, whether you can convert—that's where 'no-frills' rates make their money back. A mortgage that's 0.10% cheaper but costs five figures to break isn't cheaper. We read the fine print so the story ends where you want it to.",
    reactions: [["sarah.saves", "👏👏👏 say it louder"], ["reno.mike", "🔥 saving this"]] },
  { uncle: "Credit score doesn't matter if you've got a steady job.", age: "4h", likes: 187,
    lead: "Tell that to the lender. Your job opens the door. Your credit score picks the room.",
    reply: "Lenders read the score as a risk signal: 680 and up opens the sharpest pricing; bumps and bruises push you to a higher rate or a different kind of lender altogether. Good news—it's fixable. A few months of paying things down before you apply can move you a whole tier. Steady job plus tidy credit is the combo that gets the good shelf.",
    reactions: [["kp.northside", "🙌 the 'picks the room' line"]] },
  { uncle: "A mortgage is a mortgage. House, condo, rental—same rate.", age: "3h", likes: 96,
    lead: "And a car is a car. Try insuring a Ferrari like a Civic.",
    reply: "Owner-occupied homes get the best rates because people fight hardest to keep the roof over their own head. Rentals carry vacancy and income risk, so they cost a little more and take more paperwork. Ditto acreages, condos with quirks and anything unusual. The property is half the story—which is why we ask about it before we quote a number.",
    reactions: [["condo.curious", "👏 didn't know this"]] },
  { uncle: "Just get the 5-year fixed like everyone else.", age: "3h", likes: 158,
    lead: "Everyone else isn't making your payment.",
    reply: "Five-year fixed, three-year fixed and variable are three different products priced off three different parts of the market—so 'the rate' your neighbour quotes may not even be the thing you're shopping. Fixed buys certainty. Variable buys flexibility and, usually, a lower starting point. Shorter fixed buys a sooner do-over. The right one depends on how long you'll be in the home, not what the guy at the grill did.",
    reactions: [["renewal.ready", "🔥 'sooner do-over'"], ["m.and.j.buyahouse", "👏👏"]] },
];
function Verified() {
  return <svg className="ig-verified" viewBox="0 0 24 24" aria-label="Verified" role="img"><path fill="#0095f6" d="M12 1.5l2.6 2.2 3.4-.4 1 3.3 3 1.7-1.3 3.2 1.3 3.2-3 1.7-1 3.3-3.4-.4L12 22.5l-2.6-2.2-3.4.4-1-3.3-3-1.7L3.3 12 2 8.8l3-1.7 1-3.3 3.4.4z" /><path fill="#fff" d="M10.6 15.6l-3-3 1.2-1.2 1.8 1.8 4.6-4.6 1.2 1.2z" /></svg>;
}
function IgHeart({ n, on, onClick, testId }) {
  return <button type="button" className={`ig-heart${on ? " on" : ""}`} onClick={onClick} aria-pressed={on} aria-label="Like" data-testid={testId}><Heart size={16} fill={on ? "currentColor" : "none"} strokeWidth={1.6} />{n != null && <small>{n}</small>}</button>;
}
export function UncleThread({ place, thread = UNCLE_THREAD, visible = 3 }) {
  return <SocialThread thread={thread} visible={visible} testId="uncle-thread" commenter={{ name: "your.uncle", avatar: UNCLE }} post={{ image: "/brand/uncle-bbq-post.jpg", tag: "Free mortgage rate advice", headline: "Available at every barbecue.", caption: "Free mortgage rate advice: available at every barbecue, every long weekend, forever 🍔\n\nWhat's the wildest rate \"advice\" you've gotten from a relative? Drop it below 👇 We reply to all of them.\n\n#MortgageRates #AlbertaMortgage #LongWeekend #WinningInLife" }} pinned={{ lead: "So what's your number?", text: "Two minutes, no credit check. We'll show you the rates that are actually yours—and explain every one. Bring your uncle if you want.", likes: 1208 }} compose="Ask us what your rate actually is…" />;
}

function Caption({ text, open, setOpen }) {
  const cut = 72;
  const short = text.length > cut && !open;
  return (
    <p className="ig-cap-text">
      <b>{HANDLE}</b> {short ? text.slice(0, cut).trimEnd() + "… " : text}
      {short && <button type="button" className="ig-cap-more" onClick={() => setOpen(true)} data-testid="ig-caption-more">more</button>}
    </p>
  );
}

// Generic Instagram-style comments block. thread items: {user?, uncle|text, age, likes, lead, reply, reactions:[[user,text]]}
export function SocialThread({ post, thread, commenter, pinned, compose, visible = 3, testId = "social-thread" }) {
  const [liked, setLiked] = useState({});
  const [more, setMore] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const social = usePostSocial(post.headline || post.tag || "Hello Mortgage");
  const tog = (id) => setLiked((s) => ({ ...s, [id]: !s[id] }));
  const baseLikes = (pinned?.likes || 0) + thread.reduce((n, t) => n + (t.likes || 0), 0);
  const shown = more ? thread : thread.slice(0, visible);
  const hidden = thread.length - shown.length;
  const Av = ({ user, cls }) => commenter?.avatar && (!user || user === commenter.name) ? <img src={commenter.avatar} alt="" className={`ig-av ${cls || ""}`} /> : <span className={`ig-av ig-initial ${cls || ""}`} aria-hidden="true">{(user || "?")[0].toUpperCase()}</span>;
  return (
    <article className="ig" data-testid={testId}>
      <div className={`ig-media${capOpen ? " cap-open" : ""}`} style={{ backgroundImage: `url(${post.image})` }} onClick={() => capOpen && setCapOpen(false)}>
        <div className="ig-status"><b>11:50</b><span>100</span></div>
        <div className="ig-headline" aria-hidden="true"><span>{post.tag}</span><b>{post.headline}</b></div>
        <div className="ig-cap"><img src={HELLO} alt="" /><Caption text={post.caption} open={capOpen} setOpen={setCapOpen} /></div>
      </div>
      <div className="ig-bar">
        <PostActions s={social} title={post.headline || post.tag || "Hello Mortgage"} testId={testId} below={<p className="ig-likes" data-testid={`${testId}-likes`}>{social.followed ? <b>Following. You're officially our favourite.</b> : <><b>{(baseLikes + (social.liked ? 1 : 0)).toLocaleString("en-CA")} likes</b>{social.saved && <span> · Saved</span>}</>}</p>} />
      </div>
      <div className="ig-sheet">
        <span className="ig-grab" aria-hidden="true" />
        <h3 className="ig-title">Comments</h3>
        <ol className="ig-list">
          {shown.map((t, i) => { const user = t.user || commenter?.name || "someone"; return (
            <li key={i} className="ig-item" style={{ "--d": `${i * 80}ms` }} data-testid={`${testId}-comment-${i}`}>
              <div className="ig-c">
                <Av user={user} />
                <div className="ig-c-body">
                  <div className="ig-c-head"><b>{user}</b><span>{t.age}</span></div>
                  <p>{t.uncle || t.text}</p>
                  <CalendlyLink className="ig-reply" testId={`${testId}-replybtn-${i}`}>Reply</CalendlyLink>
                </div>
                <IgHeart n={liked[`u${i}`] ? 1 : null} on={!!liked[`u${i}`]} onClick={() => tog(`u${i}`)} testId={`${testId}-like-u${i}`} />
              </div>
              <div className="ig-c ig-nested">
                <img src={HELLO} alt="" className="ig-av sm ring" />
                <div className="ig-c-body">
                  <div className="ig-c-head"><b>{HANDLE}</b><Verified /><span>{t.age} · Author</span></div>
                  <p><a className="ig-mention" href="#c" onClick={(e) => e.preventDefault()}>@{user}</a> <strong className="ig-lead">{t.lead}</strong> {t.reply}</p>
                  <CalendlyLink className="ig-reply" testId={`${testId}-replybtn-h${i}`}>Reply</CalendlyLink>
                </div>
                <IgHeart n={t.likes + (liked[`h${i}`] ? 1 : 0)} on={!!liked[`h${i}`]} onClick={() => tog(`h${i}`)} testId={`${testId}-like-${i}`} />
              </div>
              {(t.reactions || []).map(([ru, text], j) => (
                <div className="ig-c ig-nested ig-react" key={ru}>
                  <span className="ig-av sm ig-initial" aria-hidden="true">{ru[0].toUpperCase()}</span>
                  <div className="ig-c-body">
                    <div className="ig-c-head"><b>{ru}</b><span>{t.age}</span></div>
                    <p><a className="ig-mention" href="#h" onClick={(e) => e.preventDefault()}>@{HANDLE}</a> {text}</p>
                  </div>
                  <IgHeart n={liked[`r${i}${j}`] ? 1 : null} on={!!liked[`r${i}${j}`]} onClick={() => tog(`r${i}${j}`)} testId={`${testId}-like-r${i}${j}`} />
                </div>
              ))}
            </li>
          ); })}
          {hidden > 0 && <li className="ig-more"><button type="button" onClick={() => setMore(true)} data-testid={`${testId}-view-more`}><span aria-hidden="true" />View {hidden} more comment{hidden > 1 ? "s" : ""}</button></li>}
          <li className="ig-item ig-pinned" style={{ "--d": `${shown.length * 80}ms` }}>
            <div className="ig-c">
              <img src={HELLO} alt="" className="ig-av ring" />
              <div className="ig-c-body">
                <div className="ig-c-head"><b>{HANDLE}</b><Verified /><span>Pinned · Author</span></div>
                <p><strong className="ig-lead">{pinned.lead}</strong> {pinned.text}</p>
                <CalendlyLink className="ig-reply" testId={`${testId}-reply`}>Reply</CalendlyLink>
              </div>
              <IgHeart n={pinned.likes + (liked.p ? 1 : 0)} on={!!liked.p} onClick={() => tog("p")} testId={`${testId}-like-pinned`} />
            </div>
          </li>
        </ol>
        <CalendlyLink className="ig-compose" testId={`${testId}-cta`}><img src={HELLO} alt="" className="ig-av sm" /><span>{compose}</span><b>Post</b></CalendlyLink>
      </div>
    </article>
  );
}

// Section wrapper: real <h2> above the thread for SEO + a plain-text summary the crawler can read even before hydration.
export function SocialThreadSection({ eyebrow, heading, text, dark = true, ...thread }) {
  return (
    <section className={dark ? "rt-factors" : "lab-sec"} id="the-comments" data-testid={`${thread.testId || "social-thread"}-section`}>
      <div className="wrap">
        <Intro eyebrow={eyebrow} heading={heading} text={text} />
        <SocialThread {...thread} />
      </div>
    </section>
  );
}

/* ---------- a-legacy) card thread (kept for reference) ---------- */
export function UncleThreadCards({ place }) {
  const [liked, setLiked] = useState({});
  const items = uncleItems(place);
  return (
    <article className="ut" data-testid="uncle-thread">
      <div className="ut-post">
        <img src={UNCLE} alt="" className="ut-av" />
        <div>
          <div className="ut-who"><b>Your uncle</b><span>· at the barbecue · {place || "Alberta"}</span></div>
          <p className="ut-brag">"Got 2.79% back in the day. You kids are getting robbed. Just walk into the bank and ask for the good rate."</p>
          <div className="ut-stats"><span>{items.length} replies</span><span>·</span><span>1 correction</span></div>
        </div>
      </div>
      <ol className="ut-replies">
        {items.map((it, i) => (
          <li className="ut-reply" key={it.k} style={{ "--d": `${i * 90}ms` }} data-testid={`uncle-reply-${i}`}>
            <img src={HELLO} alt="" className="ut-av sm" />
            <div className="ut-bubble">
              <div className="ut-who"><b>{HANDLE}</b><span>replying to <i>Your uncle</i></span></div>
              <blockquote className="ut-quote">"{it.myth}"</blockquote>
              <h3><span className="ut-k">{it.k}</span>{it.h}</h3>
              <p>{it.body}</p>
              <div className="ut-acts">
                <button type="button" className={`ut-like${liked[i] ? " on" : ""}`} onClick={() => setLiked((s) => ({ ...s, [i]: !s[i] }))} aria-pressed={!!liked[i]} data-testid={`uncle-like-${i}`}><Heart size={15} fill={liked[i] ? "currentColor" : "none"} />{it.likes + (liked[i] ? 1 : 0)}</button>
                <CalendlyLink className="ut-replybtn" testId={`uncle-replybtn-${i}`}><MessageCircle size={15} /> Reply</CalendlyLink>
              </div>
            </div>
          </li>
        ))}
        <li className="ut-reply ut-cta" style={{ "--d": `${items.length * 90}ms` }}>
          <img src={HELLO} alt="" className="ut-av sm" />
          <div className="ut-bubble navy">
            <div className="ut-who"><b>{HANDLE}</b><span>pinned</span></div>
            <h3>So what's <em>your</em> number?</h3>
            <p>Two minutes, no credit check. We'll show you the rates that are actually yours—and explain every one. Bring your uncle if you want.</p>
            <CalendlyLink className="btn" testId="uncle-thread-cta">Find out what mine actually is</CalendlyLink>
          </div>
        </li>
      </ol>
    </article>
  );
}

/* ---------- b) Stories ---------- */
export function UncleStories({ place }) {
  const items = uncleItems(place);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [t, setT] = useState(0);
  const raf = useRef(null);
  const DUR = 7000;
  useEffect(() => {
    let start = performance.now() - t * DUR;
    const tick = (now) => {
      if (!paused) {
        const p = Math.min(1, (now - start) / DUR);
        setT(p);
        if (p >= 1) { setI((k) => (k + 1) % items.length); start = now; setT(0); }
      } else start = now - t * DUR;
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [paused, i]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (k) => { setI((k + items.length) % items.length); setT(0); };
  const it = items[i];
  return (
    <article className="us-stories" data-testid="uncle-stories" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="us-frame" style={{ "--k": i }}>
        <div className="us-bars" aria-hidden="true">{items.map((_, k) => <span key={k}><i style={{ transform: `scaleX(${k < i ? 1 : k === i ? t : 0})` }} /></span>)}</div>
        <header className="us-head">
          <img src={HELLO} alt="" className="us-av" /><b>{HANDLE}</b><span>{i + 1}/{items.length}</span>
          <button type="button" className="us-pause" onClick={() => setPaused((p) => !p)} aria-label={paused ? "Play" : "Pause"} data-testid="uncle-stories-pause">{paused ? <Play size={16} /> : <Pause size={16} />}</button>
        </header>
        <button type="button" className="us-tap l" onClick={() => go(i - 1)} aria-label="Previous story" data-testid="uncle-stories-prev" />
        <button type="button" className="us-tap r" onClick={() => go(i + 1)} aria-label="Next story" data-testid="uncle-stories-next" />
        <div className="us-body" key={i}>
          <div className="us-uncle"><img src={UNCLE} alt="" /><div><span>Your uncle, at the barbecue</span><q>{it.myth}</q></div></div>
          <span className="us-k">{it.k} · the real story</span>
          <h3>{it.h}</h3>
          <p>{it.body}</p>
        </div>
        <footer className="us-foot">
          <CalendlyLink className="us-replybar" testId="uncle-stories-reply"><span>Ask us about your rate…</span><Send size={18} /></CalendlyLink>
        </footer>
      </div>
      <p className="us-hint">Tap the sides to move · hover to pause · {items.length} stories</p>
    </article>
  );
}

/* ---------- c) Flip cards ---------- */
export function FlipGrid({ items, frontLabel = "You've heard", backLabel = HANDLE, frontAvatar = UNCLE, testId = "flip-grid" }) {
  const [open, setOpen] = useState({});
  const flip = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  return (
    <div className="uf" data-testid={testId}>
      {items.map((it, k) => (
        <div role="button" tabIndex={0} className={`uf-card${open[k] ? " on" : ""}`} key={it.k} onClick={() => flip(k)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); flip(k); } }} aria-pressed={!!open[k]} style={{ "--d": `${k * 70}ms` }} data-testid={`${testId}-${k}`}>
          <span className="uf-face front">
            <span className="uf-top">{frontAvatar ? <img src={frontAvatar} alt="" /> : <span className="uf-dot" />}<b>{frontLabel}</b></span>
            <span className="uf-myth">"{it.front}"</span>
            <span className="uf-flip"><RotateCcw size={14} /> Tap for the real story</span>
            <span className="uf-n">0{k + 1}</span>
          </span>
          <span className="uf-face back">
            <span className="uf-top"><img src={HELLO} alt="" /><b>{backLabel}</b></span>
            <span className="uf-k">{it.k}</span>
            <span className="uf-p">{it.back}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
export function ClosingCostsFlip() {
  return (
    <section className="lab-sec alt" id="closing-costs" data-testid="closing-costs-section">
      <div className="wrap">
        <Intro eyebrow="Closing costs in Alberta" heading={<>Closing costs you forgot. <em>And the one you don't have.</em></>} text="Six things that show up on your lawyer's statement between the accepted offer and the keys. Tap a card for what it actually runs in Alberta." />
        <FlipGrid items={CLOSING_COST_CARDS} frontLabel="You've heard" frontAvatar={null} testId="closing-flip" />
      </div>
    </section>
  );
}

export function UncleFlipGrid({ place }) {
  return <FlipGrid frontLabel="Your uncle says" testId="uncle-flip-grid" items={uncleItems(place).map((it) => ({ k: it.k, front: it.myth, back: `${it.h} ${it.body}` }))} />;
}
